import React, { useRef, useState } from "react";
import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Add as AddIcon, Close as CloseIcon, Send as SendIcon } from "@mui/icons-material";
import { INDIGO, SLATE } from "../../constants/Colors";

const ACCEPTED_MIME = "image/png,image/jpeg,image/webp,image/gif";
const MAX_ATTACHMENTS = 5;


export default function ChatInput({ onSend, disabled = false }) {
  const [value, setValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // [{ file, preview }]
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => {
      const slots = MAX_ATTACHMENTS - prev.length;
      if (slots <= 0) return prev;
      const newEntries = files.slice(0, slots).map((file) => ({ file, preview: URL.createObjectURL(file) }));
      return [...prev, ...newEntries];
    });
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setSelectedFiles((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSend = () => {
    const trimmed = value.trim();
    if ((!trimmed && selectedFiles.length === 0) || disabled) return;
    onSend(trimmed, selectedFiles);
    setValue("");
    setSelectedFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length === 0) return;
    e.preventDefault();
    setSelectedFiles(prev => {
      const slots = MAX_ATTACHMENTS - prev.length;
      if (slots <= 0) return prev;
      const newEntries = imageFiles.slice(0, slots).map(file => ({ file, preview: URL.createObjectURL(file) }));
      return [...prev, ...newEntries];
    });
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${SLATE[200]}`,
        bgcolor: "#fff",
        px: 3,
        pt: 2,
        pb: 2.5,
        flexShrink: 0,
      }}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME}
        multiple
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      {/* Image preview strip */}
      {selectedFiles.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {selectedFiles.map(({ preview }, idx) => (
            <Box key={idx} sx={{ position: "relative", display: "inline-flex" }}>
              <Box
                component="img"
                src={preview}
                alt={`attachment-${idx}`}
                sx={{
                  width: 72,
                  height: 72,
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: `1px solid ${SLATE[200]}`,
                }}
              />
              <IconButton
                size="small"
                onClick={() => removeFile(idx)}
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 18,
                  height: 18,
                  bgcolor: SLATE[700],
                  color: "#fff",
                  "&:hover": { bgcolor: "#ef4444" },
                }}
              >
                <CloseIcon sx={{ fontSize: "0.7rem" }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Text input row */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField
          multiline
          maxRows={5}
          fullWidth
          placeholder="Type your answer... (Shift+Enter for new line)"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="Attach image" placement="top">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || selectedFiles.length >= MAX_ATTACHMENTS}
                    size="small"
                    sx={{
                      color: SLATE[500],
                      borderRadius: "8px",
                      p: "6px",
                      "&:hover": { bgcolor: SLATE[100], color: SLATE[900] },
                    }}
                  >
                    <AddIcon sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.875rem",
              bgcolor: "#fff",
              "& fieldset": { borderColor: SLATE[300] },
              "&:hover fieldset": { borderColor: SLATE[400] },
              "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
            },
            "& .MuiInputBase-input": { color: SLATE[800], py: "12px" },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={(!value.trim() && selectedFiles.length === 0) || disabled}
          endIcon={<SendIcon sx={{ fontSize: "1rem !important" }} />}
          sx={{
            background: `linear-gradient(135deg, ${INDIGO[500]} 0%, #7c3aed 100%)`,
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 600,
            px: 2.5,
            py: "12px",
            flexShrink: 0,
            boxShadow: "none",
            "&:hover": {
              background: `linear-gradient(135deg, ${INDIGO[600]} 0%, #6d28d9 100%)`,
              boxShadow: "none",
            },
            "&.Mui-disabled": { opacity: 0.5, background: SLATE[300] },
          }}
        >
          Send
        </Button>
      </Box>

      {/* Footer hint */}
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: SLATE[400],
          textAlign: "center",
          mt: 1.5,
        }}
      >
        Answer a few questions to create your agent, or click &quot;Build Manually&quot; to configure it yourself
      </Typography>
    </Box>
  );
}
